import React, { useCallback, useEffect, useMemo, useState } from "react";
import ForecastDashboard from "./ForecastDashboard";
import EconModal from "./EconModal";
import LcsHeroChart from "./LcsHeroChart";
import LcsPortfolioTable from "./LcsPortfolioTable";
import useLcsLeaseOverlay from "../services/useLcsLeaseOverlay";
import { buildRealLeaseFromView } from "../services/lcsViewModel";
import { inferLeaseCommentary, profileFromLive } from "../services/lcsInference";
import { resolveDecisionProfile } from "./lcsDecisionProfiles";
import RANKED_LEASES_STAGED from "../data/lcsStagedLeases.json";
import RANKED_LEASES_FULL from "../data/lcsRankedLeases.json";
import RANKED_LEASES_FOCUSED from "../data/lcsFocusedLeases.json";
// Live per-lease ageDays map written by scripts/synthesize_lcs_cards.mjs.
// Used to override the bundle's stale modelAgeDays in the left rail so
// freshness is consistent with the right-panel/header.
import LCS_LIVE_AGE_DAYS from "../data/lcsLeaseAgeDays.json";
// Minimal "operator intelligence console" right-panel format. Hand-authored
// for the top 5 LCS leases; every other lease is adapted from its decisionCard
// at runtime via lcsMinimalAdapter. Set USE_MINIMAL_DECISION_PANEL to false
// to fall back to the dense DecisionCard for all leases.
import { MINIMAL_LCS_DECISION_PROFILES } from "../data/minimalLcsDecisionProfiles";
import { adaptDecisionCardToMinimal } from "../services/lcsMinimalAdapter";
import "./ForecastCockpit.css";

const USE_MINIMAL_DECISION_PANEL = true;

/* ═══════════════════════════════════════════════════════════════════════════
 * ForecastDashboardDecisionUI.js — LCS Operational Intelligence Cockpit
 *
 * Visual lineage: ForecastCockpit (the dark luminescent demo cockpit). Reuses
 * its ck-* class system from ForecastCockpit.css.
 * Data lineage:   real LCS overlay_staged via useLcsLeaseOverlay + bundled
 *                 RANKED_LEASES (offline-ranked staged producers) + the
 *                 deterministic inference layer.
 *
 * Layout:  [thin top telemetry] / [breadcrumb] /
 *          [icon rail | left lease ranking | hero chart center | decision right]
 *          [hidden ForecastDashboard providing Context for EconModal etc.]
 *
 * Selected-lease single source of truth: `selectedLeaseId`. selectLease()
 * writes URL params synchronously before setState so any context consumer
 * that reads URL on mount gets the right lease.
 * ═══════════════════════════════════════════════════════════════════════════ */

// ── Bundle pick: stage-discovered (default), focused-25, or broader-60. ──
function pickBundle() {
  try {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get("focused") === "1") return RANKED_LEASES_FOCUSED;
    if (qs.get("focused") === "0") return RANKED_LEASES_FULL;
  } catch { /* noop */ }
  return RANKED_LEASES_STAGED;
}
const RANKED_LEASES = pickBundle();
const DEFAULT_LCS_LEASE =
  RANKED_LEASES.find(
    (l) => l.assetTypeHint === "producer" && (l.forecastRows || 0) > 0,
  )?.leaseId || "CARTERBF";

// ── Helpers ──
const fin = (n) => Number.isFinite(n);
const money = (n, d = 0) =>
  fin(n)
    ? new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: d }).format(n)
    : "—";
const fmtN = (n, d = 1) =>
  fin(n) ? new Intl.NumberFormat(undefined, { maximumFractionDigits: d }).format(n) : "—";

const RECO_TIER = (rec) =>
  rec === "Optimize" ? "good"
    : rec === "Investigate Decline" ? "warn"
      : rec === "Monitor" ? "warn"
        : rec === "Insufficient Data" ? "dim"
          : "dim";

// Score "ring" color tier (matches ForecastCockpit's ck-score-* tiers).
const SCORE_TIER = (s) => (s >= 75 ? "crit" : s >= 55 ? "warm" : "hot");

// Sparkline — small inline SVG, deterministic from lease's score/rate.
function Spark({ positive, score = 50 }) {
  const seed = (score * 137) % 500;
  const pts = Array.from({ length: 14 }, (_, i) => {
    const v = Math.sin((seed + i * 19) / 7) * 0.5 + 0.5;
    return [i * 5, 14 - v * 12];
  });
  const d = pts.reduce(
    (acc, [x, y], i) => acc + (i === 0 ? `M${x},${y}` : ` L${x},${y}`),
    "",
  );
  return (
    <svg className="ck-spark" width="70" height="14" viewBox="0 0 65 14">
      <path
        d={d}
        fill="none"
        stroke={positive ? "#5aeaa8" : "#f8a8a8"}
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── DecisionCard ──────────────────────────────────────────────────────────
// Structured operator-first decision card. Renders the LCS_DECISION_CARDS
// schema (status, primaryMove, decisionLine, changeSummary, productionSignals,
// moneyLever, nextActions, cautionFlags, evidence) as a compact command-center
// block — chips and short bullets, no paragraphs. The old prose narrative
// (whyItMatters, operatorTone) collapses into a Detail drawer at the bottom.
//
// All styling is inline so this can ship without touching the global CSS.
// ─────────────────────────────────────────────────────────────────────────
function DecisionCard({
  card,
  ledger,
  priority,
  confidence,
  priorityScore,
  freshness,
  recoWhy,
  recoOperatorTone,
  onSimulate,
  onPortfolio,
}) {
  // Operator-grade 4-card layout:
  //   1. COMMAND   — what to do, why, who is doing it (status/priority/confidence chips + primary move + decision line)
  //   2. VALUE     — money lever + ledger collapsed into one compact card
  //   3. SIGNALS   — 4-5 chips merging changeSummary + meaningful caution
  //   4. ACTIONS   — 3 checkbox actions + 1 metadata line
  // Below: collapsed Production Detail / Action Plan Detail / Evidence / Ledger Detail.
  // ────────────────────────────────────────────────────────────────────────
  const statusKey = String(card?.status || "").toUpperCase();
  const statusColors =
    /RESOLVED|OPTIMIZ/.test(statusKey)         ? { bg: "rgba(110,180,140,0.14)", fg: "#9ed4b3", border: "rgba(110,180,140,0.32)" } :
    /INVESTIGATE|DATA QUALITY/.test(statusKey) ? { bg: "rgba(232,150,85,0.14)",  fg: "#e8b890", border: "rgba(232,150,85,0.32)" } :
    /DECISION REQUIRED/.test(statusKey)        ? { bg: "rgba(232,85,85,0.14)",   fg: "#e88888", border: "rgba(232,85,85,0.32)"  } :
                                                  { bg: "rgba(120,140,180,0.14)", fg: "#a8b8d0", border: "rgba(120,140,180,0.30)" };

  const verdictColor = (v) => {
    const s = String(v || "").toLowerCase();
    if (/improv|reversed|stabilized|refresh|10×/.test(s))               return { bg: "rgba(110,180,140,0.14)", fg: "#9ed4b3" };
    if (/escal|recur|chronic|stuck|active|leak|down|mechanic/.test(s))  return { bg: "rgba(232,85,85,0.14)",   fg: "#e88888" };
    if (/resolv|settl|tight|stable/.test(s))                            return { bg: "rgba(160,180,210,0.14)", fg: "#b8c6dc" };
    return { bg: "rgba(200,170,50,0.10)", fg: "#d8c050" };
  };

  const signalColor = (s) => {
    const t = String(s || "").toLowerCase();
    if (/clean|stable|recover|improv/.test(t))           return "#9ed4b3";
    if (/leaking|low|suspect|volatile|chronic/.test(t))  return "#e88888";
    if (/settl|tight|recovery|positive/.test(t))         return "#b8c6dc";
    return "#a8b8d0";
  };

  const freshnessColor = freshness?.status === "Stale"          ? "#e8b890"
                       : freshness?.status === "Slightly Stale" ? "#d8c050"
                                                                : "#9ed4b3";

  const monoFont = "'JetBrains Mono', monospace";

  // ── Derived ledger fields (compact value card) ──────────────────────────
  const ledgerOutcome = ledger?.actualOutcomeWindow || {};
  const ledgerRoi = ledger?.roiEstimate || {};
  const ledgerActionStatus = String(ledger?.actionStatus?.status || "").toUpperCase();
  const actionStatusColor =
    /BLOCKED/.test(ledgerActionStatus)            ? "#e8b890" :
    /PENDING (FIELD|DECISION)/.test(ledgerActionStatus) ? "#e88888" :
    /STABILIZATION/.test(ledgerActionStatus)      ? "#d8c050" :
    /PROPOSED/.test(ledgerActionStatus)           ? "#9ed4b3" :
                                                    "#a8b8d0";
  const cantSize = !ledgerRoi.valueRange || ledgerRoi.valueRange === "Cannot size";
  const moneyValueColor = cantSize ? "#a8b8d0" : "#9ed4b3";

  // Dedupe "directional": if the value text already says "directional",
  // suppress the redundant " · directional" suffix from the confidence chip.
  const valueRangeText = ledgerRoi.valueRange || card.moneyLever?.upside || "—";
  const valueAlreadySaysDirectional = /directional/i.test(valueRangeText);
  const showConfidenceSuffix = ledgerRoi.confidence && ledgerRoi.confidence !== "n/a" && !valueAlreadySaysDirectional;

  // ── Action metadata as chips (Owner · Timing · Recheck) ────────────────
  const firstAction = ledger?.actionPlan?.[0] || null;
  const actionChips = [];
  if (firstAction?.ownerRole)  actionChips.push({ label: "Owner",   value: firstAction.ownerRole });
  if (firstAction?.urgency)    actionChips.push({ label: "Timing",  value: firstAction.urgency });
  if (ledgerOutcome.recheckAfterDays != null) {
    actionChips.push({ label: "Recheck", value: `${ledgerOutcome.recheckAfterDays}d` });
  }

  // ── Caution promotion: lift critical caution chips into the COMMAND
  //    header for risky leases. We classify a card as "risky" if status is
  //    INVESTIGATE / DECISION REQUIRED / DATA QUALITY, or if specific
  //    caution flags signal data/measurement/MAPE problems.
  const isRiskyStatus = /INVESTIGATE|DECISION REQUIRED|DATA QUALITY|MEASUREMENT|POST-WORKOVER|POST-RIG/i.test(statusKey);
  const promoteCautionPattern = /MAPE \d{2,}|stale|asleep|no data|sparse|tubing leak|backside|recurring|chronic|measurement|leak|belt|water on oil|workover|rig/i;
  const promotedCaution = (card?.cautionFlags || [])
    .filter(f => promoteCautionPattern.test(String(f)))
    .slice(0, 2);
  // Only promote when the card is risky AND we found something pattern-worthy.
  const showPromotedCaution = isRiskyStatus && promotedCaution.length > 0;

  // ── Section helpers ────────────────────────────────────────────────────
  const SectionLabel = ({ children, color = "#566580" }) => (
    <div style={{
      fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", color,
      fontFamily: monoFont, textTransform: "uppercase", marginBottom: 6,
    }}>{children}</div>
  );

  const cardWrapStyle = {
    padding: "10px 12px", borderRadius: 5, marginBottom: 8,
    background: "rgba(15,24,42,0.55)",
    border: "1px solid rgba(50,70,100,0.28)",
  };

  return (
    <div className="ck-decision-block" style={{ paddingBottom: 6, padding: 0, background: "transparent", border: "none" }}>

      {/* ════════════════ 1. COMMAND CARD ════════════════ */}
      <div style={{ ...cardWrapStyle, background: "rgba(20,30,52,0.65)", position: "relative" }}>
        {/* Score in the top-right corner — secondary, low-key (not a chip). */}
        {priorityScore != null && (
          <span title="Decision Inference Engine score (0-100)" style={{
            position: "absolute", top: 9, right: 12,
            fontSize: 9, fontWeight: 500, color: "#475670",
            fontFamily: monoFont, letterSpacing: "0.02em",
          }}>score {priorityScore}</span>
        )}
        <SectionLabel>COMMAND</SectionLabel>

        {/* Status / priority / confidence / freshness chip strip — no score */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          <span style={{
            fontSize: 9, fontWeight: 800, letterSpacing: "0.06em", padding: "3px 8px",
            borderRadius: 3, fontFamily: monoFont,
            background: statusColors.bg, color: statusColors.fg, border: `1px solid ${statusColors.border}`,
          }}>{card.status}</span>
          {priority && (
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", padding: "3px 7px",
              borderRadius: 3, fontFamily: monoFont,
              color: priority === "High" ? "#e88888" : priority === "Medium" ? "#d8c050" : "#7888a0",
              border: `1px solid ${priority === "High" ? "rgba(232,85,85,0.30)" : priority === "Medium" ? "rgba(200,170,50,0.30)" : "rgba(120,140,180,0.25)"}`,
            }}>{String(priority).toUpperCase()}</span>
          )}
          {confidence && (
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", padding: "3px 7px",
              borderRadius: 3, fontFamily: monoFont, color: "#a8b8d0",
              border: "1px solid rgba(120,140,180,0.25)",
            }}>{String(confidence).toUpperCase()} CONF</span>
          )}
          {freshness?.status && (
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: "0.04em",
              padding: "3px 7px", borderRadius: 3, fontFamily: monoFont,
              color: freshnessColor, border: `1px solid ${freshnessColor}33`,
            }}>{freshness.status === "Fresh" ? "● FRESH" : freshness.status === "Stale" ? `STALE ${freshness.ageDays}d` : `${freshness.ageDays}d`}</span>
          )}
        </div>

        {/* Primary move — biggest text on the panel */}
        <div style={{
          fontSize: 16, fontWeight: 700, color: "#f0f4fa", lineHeight: 1.22,
          marginBottom: 6, letterSpacing: "-0.01em",
        }}>{card.primaryMove}</div>

        {/* Decision line */}
        <div style={{ fontSize: 11.5, color: "#a8b8d0", lineHeight: 1.4 }}>
          {card.decisionLine}
        </div>

        {/* Promoted caution — only for risky leases. Lifts critical caution
            chips (stale/MAPE/measurement/leak) into the COMMAND card so the
            operator never has to expand a drawer to see a red flag. */}
        {showPromotedCaution && (
          <div style={{
            display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8,
            paddingTop: 8, borderTop: "1px solid rgba(232,150,85,0.20)",
          }}>
            {promotedCaution.map((f, i) => (
              <span key={i} style={{
                fontSize: 9, fontWeight: 700, letterSpacing: "0.03em",
                padding: "2px 7px", borderRadius: 3, fontFamily: monoFont,
                background: "rgba(232,150,85,0.12)", color: "#e8b890",
                border: "1px solid rgba(232,150,85,0.30)",
              }}>⚠ {f}</span>
            ))}
          </div>
        )}

        {/* CTA buttons */}
        <div className="ck-cta-row" style={{ marginTop: 10, marginBottom: 0 }}>
          <button className="ck-cta-1" onClick={onSimulate}>SIMULATE</button>
          <button className="ck-cta-2" onClick={onPortfolio}>PORTFOLIO</button>
        </div>
      </div>

      {/* ════════════════ 2. VALUE CARD (money + ledger merged) ════════════════ */}
      <div style={{
        ...cardWrapStyle,
        background: cantSize ? "rgba(50,70,100,0.20)" : "rgba(35,75,90,0.30)",
        border: `1px solid ${cantSize ? "rgba(120,140,180,0.25)" : "rgba(90,184,201,0.30)"}`,
      }}>
        <SectionLabel color={cantSize ? "#7888a0" : "#5ab8c9"}>VALUE</SectionLabel>

        {/* Money line — large value */}
        <div style={{
          fontSize: 15, fontWeight: 700, color: moneyValueColor,
          fontFamily: monoFont, marginBottom: 4, letterSpacing: "-0.01em",
        }}>
          {cantSize ? "Cannot size yet" : valueRangeText}
          {showConfidenceSuffix && (
            <span style={{ fontSize: 10, color: "#7888a0", fontStyle: "italic", fontWeight: 500, marginLeft: 8 }}>
              · {ledgerRoi.confidence}
            </span>
          )}
        </div>

        {/* Blocker (only when Cannot Size) — explains WHY no dollar can be sized.
            Pulled from ledger.actionStatus.reason which already explains the
            blocker contextually per status type. */}
        {cantSize && ledger?.actionStatus?.reason && (
          <div style={{
            fontSize: 11, color: "#e8b890", marginBottom: 6,
            display: "flex", alignItems: "baseline", gap: 6,
          }}>
            <span style={{
              color: "#e8b890", fontFamily: monoFont, fontSize: 9,
              letterSpacing: "0.06em", flexShrink: 0,
            }}>BLOCKER</span>
            <span style={{ color: "#d4c0a8" }}>{ledger.actionStatus.reason}</span>
          </div>
        )}

        {/* Lever */}
        {card.moneyLever?.label && (
          <div style={{
            fontSize: 11, color: "#c8d8e8", marginBottom: 6,
            display: "flex", alignItems: "baseline", gap: 6,
          }}>
            <span style={{
              color: "#7888a0", fontFamily: monoFont, fontSize: 9,
              letterSpacing: "0.06em", flexShrink: 0,
            }}>LEVER</span>
            <span>{card.moneyLever.label}</span>
          </div>
        )}

        {/* Tracking + recheck + outcome — single compact line */}
        {ledger && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
            fontSize: 10, fontFamily: monoFont, color: "#7888a0",
            paddingTop: 6, borderTop: "1px solid rgba(50,70,100,0.30)",
          }}>
            <span style={{
              fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 3,
              letterSpacing: "0.04em",
              color: actionStatusColor, border: `1px solid ${actionStatusColor}33`,
              background: `${actionStatusColor}14`,
            }}>{ledger.actionStatus?.status || "PROPOSED"}</span>
            {ledgerOutcome.recheckAfterDays && (
              <span>recheck <span style={{ color: "#c8d8e8" }}>{ledgerOutcome.recheckAfterDays}d</span></span>
            )}
            <span style={{ marginLeft: "auto" }}>
              outcome <span style={{ color: "#d8c050" }}>{(ledgerOutcome.status || "pending").toLowerCase()}</span>
            </span>
          </div>
        )}

        {/* Caveat (small italic) */}
        {card.moneyLever?.caveat && (
          <div style={{ fontSize: 10, color: "#8896ac", marginTop: 6, fontStyle: "italic", lineHeight: 1.35 }}>
            {card.moneyLever.caveat}
          </div>
        )}
      </div>

      {/* ════════════════ 3. SIGNALS CARD ════════════════ */}
      {Array.isArray(card.changeSummary) && card.changeSummary.length > 0 && (
        <div style={cardWrapStyle}>
          <SectionLabel>SIGNALS</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {card.changeSummary.slice(0, 4).map((c, i) => {
              const v = verdictColor(c.verdict);
              const hasBeforeAfter = c.before && c.before !== "—" && c.before !== "-";
              // Flex-wrap row: when the value is long (e.g. "haul paperwork
              // → meter asleep ×3d"), the verdict chip wraps cleanly to the
              // next line instead of overlapping the value.
              return (
                <div key={`chg-${i}`} className="ck-signal-row" style={{
                  display: "flex", flexWrap: "wrap", alignItems: "center",
                  rowGap: 3, columnGap: 8,
                  fontSize: 10.5, fontFamily: monoFont, lineHeight: 1.4,
                }}>
                  <span style={{
                    flex: "0 0 auto", minWidth: 80,
                    color: "#7888a0", fontSize: 9, letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}>{c.label}</span>
                  <span style={{
                    flex: "1 1 140px", minWidth: 0,
                    color: "#c8d8e8", overflowWrap: "anywhere", wordBreak: "break-word",
                  }}>
                    {hasBeforeAfter ? (
                      <>
                        <span style={{ color: "#7888a0" }}>{c.before}</span>
                        <span style={{ color: "#566580", margin: "0 6px" }}>→</span>
                        <span style={{ color: "#e8eef6" }}>{c.after}</span>
                      </>
                    ) : (
                      <span style={{ color: "#e8eef6" }}>{c.after}</span>
                    )}
                  </span>
                  {c.verdict && (
                    <span style={{
                      flex: "0 0 auto",
                      padding: "1px 7px", borderRadius: 3,
                      fontSize: 9, fontWeight: 700, letterSpacing: "0.04em",
                      background: v.bg, color: v.fg, whiteSpace: "nowrap",
                      marginLeft: "auto",
                    }}>{c.verdict}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ════════════════ 4. ACTIONS CARD ════════════════ */}
      {Array.isArray(card.nextActions) && card.nextActions.length > 0 && (
        <div style={cardWrapStyle}>
          <SectionLabel>ACTIONS</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {card.nextActions.slice(0, 3).map((a, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "flex-start", gap: 7,
                fontSize: 11.5, color: "#d8e2ee", lineHeight: 1.4,
              }}>
                <span style={{
                  display: "inline-block", width: 11, height: 11, marginTop: 3,
                  border: "1px solid #566580", borderRadius: 2, flexShrink: 0,
                }} />
                <span>{a}</span>
              </div>
            ))}
          </div>

          {/* Owner / Timing / Recheck rendered as compact chips, not faint footer */}
          {actionChips.length > 0 && (
            <div style={{
              display: "flex", flexWrap: "wrap", gap: 6,
              marginTop: 8, paddingTop: 7, borderTop: "1px solid rgba(50,70,100,0.25)",
            }}>
              {actionChips.map((c, i) => (
                <span key={i} style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  fontSize: 9, fontWeight: 600, letterSpacing: "0.03em",
                  padding: "3px 8px", borderRadius: 3, fontFamily: monoFont,
                  background: "rgba(50,70,100,0.30)", color: "#c8d8e8",
                  border: "1px solid rgba(120,140,180,0.20)",
                  whiteSpace: "nowrap",
                }}>
                  <span style={{ color: "#7888a0", textTransform: "uppercase" }}>{c.label}</span>
                  <span>{c.value}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ════════════════ COLLAPSIBLE DETAIL DRAWERS ════════════════ */}
      {Array.isArray(card.productionSignals) && card.productionSignals.length > 0 && (
        <details style={{ marginTop: 4 }}>
          <summary style={{
            cursor: "pointer", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
            color: "#566580", fontFamily: monoFont, textTransform: "uppercase", padding: "4px 0",
          }}>▸ Production detail</summary>
          <div style={{ marginTop: 4, padding: "6px 10px", display: "flex", flexDirection: "column", gap: 4 }}>
            {card.productionSignals.map((p, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "baseline", gap: 6, fontSize: 10,
                fontFamily: monoFont,
              }}>
                <span style={{ minWidth: 36, color: "#7888a0", fontSize: 9, letterSpacing: "0.04em" }}>
                  {String(p.stream).toUpperCase()}
                </span>
                <span style={{ color: "#c8d8e8" }}>{p.value}</span>
                {p.signal && p.signal !== "n/a" && (
                  <span style={{ color: signalColor(p.signal), fontWeight: 600 }}>{p.signal}</span>
                )}
                {p.caution && (
                  <span style={{ color: "#7888a0", fontStyle: "italic" }}>· {p.caution}</span>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      {Array.isArray(ledger?.actionPlan) && ledger.actionPlan.length > 0 && (
        <details style={{ marginTop: 2 }}>
          <summary style={{
            cursor: "pointer", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
            color: "#566580", fontFamily: monoFont, textTransform: "uppercase", padding: "4px 0",
          }}>▸ Action plan detail ({ledger.actionPlan.length})</summary>
          <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 6 }}>
            {ledger.actionPlan.map((a, i) => (
              <div key={i} style={{
                padding: "6px 10px", borderRadius: 3,
                background: "rgba(15,24,42,0.50)",
                border: "1px solid rgba(50,70,100,0.20)",
              }}>
                <div style={{ fontSize: 11, color: "#e8eef6", fontWeight: 600, marginBottom: 2 }}>{a.title}</div>
                <div style={{ fontSize: 9, color: "#7888a0", fontFamily: monoFont, letterSpacing: "0.04em", marginBottom: 3 }}>
                  {a.ownerRole} · {a.urgency} · recheck {a.recheckAfterDays}d
                </div>
                {Array.isArray(a.checklist) && a.checklist.length > 0 && (
                  <ul style={{ margin: "2px 0", paddingLeft: 18, color: "#a8b8d0", fontSize: 10, lineHeight: 1.45 }}>
                    {a.checklist.map((c, j) => <li key={j}>{c}</li>)}
                  </ul>
                )}
                {a.successCriteria && (
                  <div style={{ fontSize: 9, color: "#7888a0", fontStyle: "italic", marginTop: 3, lineHeight: 1.35 }}>
                    Success: {a.successCriteria}
                  </div>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      {Array.isArray(card.cautionFlags) && card.cautionFlags.length > 0 && (
        <details style={{ marginTop: 2 }}>
          <summary style={{
            cursor: "pointer", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
            color: "#566580", fontFamily: monoFont, textTransform: "uppercase", padding: "4px 0",
          }}>▸ Caution flags ({card.cautionFlags.length})</summary>
          <div className="ck-evidence-chips" style={{ marginTop: 6 }}>
            {card.cautionFlags.map((f, i) => (
              <span key={i} className="ck-ev-chip">{f}</span>
            ))}
          </div>
        </details>
      )}

      {(Array.isArray(card.evidence) && card.evidence.length > 0) || recoWhy || recoOperatorTone ? (
        <details style={{ marginTop: 2 }}>
          <summary style={{
            cursor: "pointer", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
            color: "#566580", fontFamily: monoFont, textTransform: "uppercase", padding: "4px 0",
          }}>▸ Evidence + curated narrative</summary>
          {Array.isArray(card.evidence) && card.evidence.length > 0 && (
            <ul style={{ margin: "6px 0 0", paddingLeft: 16, color: "#8896ac", fontSize: 10, lineHeight: 1.5, fontFamily: monoFont }}>
              {card.evidence.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
          {recoOperatorTone && (
            <div style={{
              marginTop: 8, padding: "5px 8px",
              background: "rgba(90,184,201,0.06)",
              borderLeft: "2px solid rgba(90,184,201,0.4)",
              fontSize: 11, fontStyle: "italic", color: "#a8c0d8", lineHeight: 1.35,
            }}>"{recoOperatorTone}"</div>
          )}
          {recoWhy && (
            <div style={{ marginTop: 8, fontSize: 11, color: "#8896ac", lineHeight: 1.45 }}>{recoWhy}</div>
          )}
        </details>
      ) : null}

      {ledger && Array.isArray(ledger.actualOutcomeWindow?.metricsToWatch) && (
        <details style={{ marginTop: 2 }}>
          <summary style={{
            cursor: "pointer", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
            color: "#566580", fontFamily: monoFont, textTransform: "uppercase", padding: "4px 0",
          }}>▸ Ledger detail</summary>
          <div style={{ marginTop: 6, fontSize: 10, color: "#a8b8d0", lineHeight: 1.5, fontFamily: monoFont }}>
            <div><span style={{ color: "#7888a0" }}>Tracking from </span>{ledger.generatedAt}</div>
            <div><span style={{ color: "#7888a0" }}>Watch </span>{ledger.actualOutcomeWindow.metricsToWatch.join(" · ")}</div>
            <div><span style={{ color: "#7888a0" }}>Expected </span>{ledger.actualOutcomeWindow.expectedDirection}</div>
            <div style={{ marginTop: 4, fontStyle: "italic", color: "#566580" }}>{ledger.ledgerLabel || "Tracking starts from this run"}</div>
          </div>
        </details>
      )}
    </div>
  );
}

// ── MinimalDecisionPanel ──────────────────────────────────────────────────
// PROTOTYPE: ultra-minimal "operator intelligence console" right panel.
// Renders MINIMAL_LCS_DECISION_PROFILES entries only (CARTERBF, CHADR288,
// GILLUMA, NORTHW, CRICKETSU). Heavy whitespace, calm hierarchy, no chip
// soup. The existing DecisionCard renders for every other lease.
//
// Hierarchy (top → bottom):
//   [mode]  PRIMARY MOVE  (large)
//   diagnosis sentence
//   STAKES   (value / lever / caveat)
//   EVIDENCE (≤3 cards)
//   NEXT MOVE (primary + secondary + recheck)
//   subtle metadata footer
//   collapsed: production / notes / ledger
// ─────────────────────────────────────────────────────────────────────────
function MinimalDecisionPanel({ profile, onSimulate, onPortfolio }) {
  const monoFont = "'JetBrains Mono', monospace";

  const modeColors = {
    OPTIMIZE:    { fg: "#9ed4b3", bg: "rgba(110,180,140,0.10)", border: "rgba(110,180,140,0.28)" },
    INVESTIGATE: { fg: "#e88888", bg: "rgba(232,85,85,0.10)",   border: "rgba(232,85,85,0.28)"  },
    REVIEW:      { fg: "#e8b890", bg: "rgba(232,150,85,0.10)",  border: "rgba(232,150,85,0.28)" },
    MONITOR:     { fg: "#a8b8d0", bg: "rgba(120,140,180,0.10)", border: "rgba(120,140,180,0.25)" },
  };
  const mc = modeColors[profile.mode] || modeColors.MONITOR;

  const toneColor = (t) => (
    t === "positive" ? "#9ed4b3" :
    t === "negative" ? "#e88888" :
    t === "caution"  ? "#e8b890" :
                       "#a8b8d0"
  );

  const Section = ({ children }) => (
    <div style={{
      fontSize: 9, fontWeight: 700, letterSpacing: "0.10em",
      color: "#566580", fontFamily: monoFont,
      textTransform: "uppercase", marginBottom: 8,
    }}>{children}</div>
  );

  // Proper-case helper for the meta line ("HIGH" → "High")
  const titleCase = (s) => {
    if (!s) return "";
    const t = String(s).toLowerCase();
    return t.charAt(0).toUpperCase() + t.slice(1);
  };

  // Build the subtle one-line metadata: Fresh · High confidence · Medium priority · Recheck 14d
  const metaParts = [];
  if (profile.freshness)          metaParts.push(titleCase(profile.freshness));
  if (profile.confidence)         metaParts.push(`${titleCase(profile.confidence)} confidence`);
  if (profile.priority)           metaParts.push(`${titleCase(profile.priority)} priority`);
  if (profile.nextMove?.recheck)  metaParts.push(`Recheck ${profile.nextMove.recheck}`);

  const cantSize = profile.stakes?.value && /cannot size/i.test(profile.stakes.value);

  // Anchored card style — for STAKES / NEXT MOVE (slightly tighter)
  const anchorCardStyle = {
    padding: "10px 12px", borderRadius: 5, marginBottom: 12,
    background: "rgba(15,24,42,0.55)",
    border: "1px solid rgba(50,70,100,0.30)",
  };

  return (
    <div style={{ padding: "4px 0 8px", color: "#e8eef6" }}>

      {/* ════════════════ COMMAND ════════════════ */}

      {/* Mode pill (small, top) */}
      <div style={{ marginBottom: 12 }}>
        <span style={{
          fontSize: 9, fontWeight: 800, letterSpacing: "0.08em",
          padding: "3px 9px", borderRadius: 3, fontFamily: monoFont,
          background: mc.bg, color: mc.fg, border: `1px solid ${mc.border}`,
        }}>{profile.mode}</span>
      </div>

      {/* Primary move — largest text on the panel */}
      <div style={{
        fontSize: 18, fontWeight: 700, color: "#f4f7fb",
        lineHeight: 1.2, letterSpacing: "-0.012em",
        marginBottom: 10,
      }}>{profile.primaryMove}</div>

      {/* Diagnosis line — one sentence */}
      <div style={{
        fontSize: 12, color: "#a8b8d0", lineHeight: 1.5,
        marginBottom: 6,
      }}>{profile.diagnosisLine}</div>

      {/* WhyItMatters — optional one-sentence micro-line, italic muted */}
      {profile.whyItMatters && (
        <div style={{
          fontSize: 11, color: "#8896ac", fontStyle: "italic",
          lineHeight: 1.45, marginBottom: 10,
        }}>{profile.whyItMatters}</div>
      )}

      {/* META LINE — single subtle dot-separated line (no chip soup) */}
      {metaParts.length > 0 && (
        <div style={{
          fontSize: 10.5, color: "#7888a0", lineHeight: 1.5,
          fontFamily: monoFont, letterSpacing: "0.02em",
          marginBottom: 12,
        }}>{metaParts.join(" · ")}</div>
      )}

      {/* INLINE PROOF — one compact above-the-fold proof line */}
      {profile.inlineProof && (
        <div style={{
          fontSize: 10.5, fontFamily: monoFont,
          marginBottom: 14,
          display: "flex", alignItems: "baseline", gap: 7,
          paddingLeft: 8,
          borderLeft: "2px solid rgba(110,180,140,0.35)",
        }}>
          <span style={{
            color: "#9ed4b3", fontSize: 9, fontWeight: 700,
            letterSpacing: "0.06em", flexShrink: 0,
          }}>PROOF</span>
          <span style={{ color: "#c8d8e8" }}>{profile.inlineProof}</span>
        </div>
      )}

      {/* ════════════════ STAKES (anchored card) ════════════════ */}
      {profile.stakes && (
        <div style={{
          ...anchorCardStyle,
          background: cantSize ? "rgba(50,70,100,0.20)" : "rgba(35,75,90,0.22)",
          border: `1px solid ${cantSize ? "rgba(120,140,180,0.28)" : "rgba(90,184,201,0.28)"}`,
        }}>
          <Section>STAKES</Section>
          <div style={{
            fontSize: 14, fontWeight: 600, fontFamily: monoFont,
            color: cantSize ? "#a8b8d0" : "#9ed4b3",
            marginBottom: 6, letterSpacing: "-0.005em",
          }}>{profile.stakes.value}</div>
          {profile.stakes.lever && (
            <div style={{
              fontSize: 11.5, color: "#c8d8e8", marginBottom: 4,
              display: "flex", alignItems: "baseline", gap: 6,
            }}>
              <span style={{
                color: cantSize ? "#e8b890" : "#7888a0",
                fontFamily: monoFont, fontSize: 9, letterSpacing: "0.06em",
                flexShrink: 0,
              }}>{cantSize ? "BLOCKER" : "LEVER"}</span>
              <span>{profile.stakes.lever}</span>
            </div>
          )}
          {profile.stakes.caveat && (
            <div style={{
              fontSize: 10.5, color: "#7888a0", fontStyle: "italic",
              lineHeight: 1.45, marginTop: 6,
            }}>{profile.stakes.caveat}</div>
          )}
        </div>
      )}

      {/* ════════════════ NEXT MOVE (anchored card, ABOVE evidence) ════════════════ */}
      {profile.nextMove && (
        <div style={{ ...anchorCardStyle, padding: "9px 12px" }}>
          <Section>NEXT MOVE</Section>
          <div style={{
            display: "flex", alignItems: "flex-start", gap: 7,
            fontSize: 12, color: "#f0f4fa", lineHeight: 1.4,
            marginBottom: profile.nextMove.secondary ? 6 : 0,
          }}>
            <span style={{
              display: "inline-block", width: 11, height: 11, marginTop: 3,
              border: "1px solid #7888a0", borderRadius: 2, flexShrink: 0,
            }} />
            <span style={{ fontWeight: 600 }}>{profile.nextMove.primary}</span>
          </div>
          {profile.nextMove.secondary && (
            <div style={{
              display: "flex", alignItems: "flex-start", gap: 7,
              fontSize: 10.5, color: "#a8b8d0", lineHeight: 1.4,
            }}>
              <span style={{
                color: "#566580", fontSize: 9, letterSpacing: "0.04em",
                fontFamily: monoFont, marginTop: 1, flexShrink: 0, minWidth: 54,
                textTransform: "uppercase",
              }}>After that</span>
              <span>{profile.nextMove.secondary}</span>
            </div>
          )}
          {profile.nextMove.recheck && (
            <div style={{
              fontSize: 9.5, color: "#7888a0", fontFamily: monoFont,
              letterSpacing: "0.04em", marginTop: 7,
              paddingTop: 5, borderTop: "1px solid rgba(50,70,100,0.25)",
            }}>recheck <span style={{ color: "#c8d8e8" }}>{profile.nextMove.recheck}</span></div>
          )}
        </div>
      )}

      {/* CTAs — small / secondary, between Next Move and Evidence so they
          do not consume prime above-the-fold space */}
      <div className="ck-cta-row" style={{
        marginBottom: 14,
        gap: 6, fontSize: 10,
      }}>
        <button className="ck-cta-1" style={{ padding: "5px 11px", fontSize: 10 }} onClick={onSimulate}>SIMULATE</button>
        <button className="ck-cta-2" style={{ padding: "5px 11px", fontSize: 10 }} onClick={onPortfolio}>PORTFOLIO</button>
      </div>

      {/* ════════════════ EVIDENCE (3 cards max) ════════════════ */}
      {Array.isArray(profile.evidenceCards) && profile.evidenceCards.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Section>EVIDENCE</Section>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {profile.evidenceCards.slice(0, 3).map((e, i) => {
              const accent = toneColor(e.tone);
              return (
                <div key={i} style={{
                  padding: "7px 12px",
                  background: "rgba(15,24,42,0.45)",
                  borderRadius: 4,
                  borderLeft: `2px solid ${accent}`,
                }}>
                  <div style={{
                    fontSize: 10, color: "#7888a0", letterSpacing: "0.04em",
                    textTransform: "uppercase", marginBottom: 2,
                  }}>{e.title}</div>
                  <div style={{
                    fontSize: 12.5, fontWeight: 600, color: accent,
                    fontFamily: monoFont, marginBottom: 2,
                  }}>{e.main}</div>
                  {e.detail && (
                    <div style={{
                      fontSize: 10.5, color: "#a8b8d0", lineHeight: 1.4,
                    }}>{e.detail}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Collapsed detail drawers ──────────────────────────────────── */}
      {profile.detailDrawer?.production && (
        <details style={{ marginTop: 8 }}>
          <summary style={{
            cursor: "pointer", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
            color: "#566580", fontFamily: monoFont, textTransform: "uppercase",
            padding: "4px 0",
          }}>▸ Production detail</summary>
          <div style={{ marginTop: 6, fontSize: 11, color: "#a8b8d0", lineHeight: 1.5 }}>
            {profile.detailDrawer.production}
          </div>
        </details>
      )}
      {profile.detailDrawer?.notes && (
        <details style={{ marginTop: 2 }}>
          <summary style={{
            cursor: "pointer", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
            color: "#566580", fontFamily: monoFont, textTransform: "uppercase",
            padding: "4px 0",
          }}>▸ Notes evidence</summary>
          <div style={{ marginTop: 6, fontSize: 11, color: "#a8b8d0", lineHeight: 1.5 }}>
            {profile.detailDrawer.notes}
          </div>
        </details>
      )}
      {profile.detailDrawer?.ledger && (
        <details style={{ marginTop: 2 }}>
          <summary style={{
            cursor: "pointer", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
            color: "#566580", fontFamily: monoFont, textTransform: "uppercase",
            padding: "4px 0",
          }}>▸ Ledger / tracking</summary>
          <div style={{ marginTop: 6, fontSize: 11, color: "#a8b8d0", lineHeight: 1.5 }}>
            {profile.detailDrawer.ledger}
          </div>
        </details>
      )}
    </div>
  );
}

// ── Cockpit ────────────────────────────────────────────────────────────────

function CockpitInner() {
  // Selected lease — single source of truth.
  const [selectedLeaseId, setSelectedLeaseId] = useState(() => {
    try {
      const u = new URLSearchParams(window.location.search).get("LeaseID")
        || new URLSearchParams(window.location.search).get("lease");
      return u || DEFAULT_LCS_LEASE;
    } catch {
      return DEFAULT_LCS_LEASE;
    }
  });

  // selectLease — atomic switch:
  // (1) write URL params (lease + LeaseID) synchronously
  // (2) update state — triggers all dependent components to re-fetch/re-render
  const selectLease = useCallback((nextId) => {
    if (!nextId || nextId === selectedLeaseId) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("LeaseID", nextId);
      url.searchParams.set("lease", nextId);
      window.history.replaceState({}, "", url.toString());
    } catch { /* noop */ }
    setSelectedLeaseId(nextId);
  }, [selectedLeaseId]);

  // Chart controls
  const [showOil, setShowOil] = useState(true);
  const [showGas, setShowGas] = useState(true);
  const [showWater, setShowWater] = useState(true);
  const [showBands, setShowBands] = useState(true);
  const [showMA, setShowMA] = useState(false);

  const [econOpen, setEconOpen] = useState(false);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const [ranklistFilter, setRanklistFilter] = useState("");

  // Live LCS overlay for selected lease.
  const lcs = useLcsLeaseOverlay({ leaseId: selectedLeaseId, horizon: 60 });
  const view = lcs.view;

  // Real-lease object for EconModal.
  const realLease = useMemo(() => (view ? buildRealLeaseFromView(view) : null), [view]);

  // Bundle entry for the selected lease.
  const bundleEntry = useMemo(
    () => RANKED_LEASES.find((l) => l.leaseId === selectedLeaseId) || null,
    [selectedLeaseId],
  );

  // Live deterministic inference (recompute against current view + bundle).
  const inference = useMemo(() => {
    if (!bundleEntry) return null;
    const live = profileFromLive({ bundleEntry, viewModel: view, econResult: null });
    return inferLeaseCommentary(live);
  }, [bundleEntry, view]);

  // Curated per-lease decision profile (LCS_DECISION_PROFILES). Falls back to
  // null when the lease isn't in the curated set — right panel then uses the
  // deterministic inference instead.
  const curatedProfile = useMemo(
    () =>
      resolveDecisionProfile({
        leaseId: selectedLeaseId,
        liveLlm: view?.raw?.llm || lcs.raw?.llm,
        liveOverlay: view?.raw || lcs.raw,
      }),
    [selectedLeaseId, view, lcs.raw],
  );

  // Recommendation tone for the right panel — prefer curated.
  const recoTone = useMemo(() => {
    const r = curatedProfile?.primaryAction
      || inference?.recommendedAction
      || bundleEntry?.recommendation
      || "";
    if (r.includes("Optimize")) return "pos";
    if (r.includes("Shut-in") || r.includes("Water Burden")) return "neg";
    if (r.includes("Decline") || r.includes("Monitor") || r.includes("Investigate") ||
        r.includes("Review LOE") || r.includes("Anomaly") || r.includes("Data Quality")) return "warn";
    if (r === "Insufficient Data" || r === "Refresh data" || r === "Re-run backfill") return "dim";
    return "dim";
  }, [curatedProfile, inference, bundleEntry]);

  const isOperationalLease = bundleEntry?.assetTypeHint && bundleEntry.assetTypeHint !== "producer";

  // ── Producer ranking shown in the left rail. Operational/skip leases are
  //    hidden here; user can browse them via the full portfolio modal. ──
  const rankedRows = useMemo(() => {
    const q = ranklistFilter.trim().toLowerCase();
    let rows = (RANKED_LEASES || []).filter(
      (l) => l.assetTypeHint === "producer" && (l.forecastRows || 0) > 0,
    );
    if (q) {
      rows = rows.filter(
        (l) =>
          (l.leaseId || "").toLowerCase().includes(q) ||
          (l.leaseName || "").toLowerCase().includes(q),
      );
    }
    return rows;
  }, [ranklistFilter]);

  // KPI strip values (per-stream recent rates from view-model).
  const kpiRows = useMemo(() => {
    if (!view) return [];
    return view.streams.map((s) => ({
      stream: s.name,
      latest: s.actual?.latest ?? null,
      mean7: s.actual?.last7Mean ?? null,
      trend: s.actual?.trendPct ?? null,
      unit:
        s.name === "Oil" ? "bbl/d" : s.name === "Gas" ? "mcf/d" : "bbl/d",
      present: s.present,
    }));
  }, [view]);

  // ── Ensure URL has LeaseID + Company + UserID for the embedded
  //    ForecastDashboard that lives hidden below the cockpit (it provides the
  //    Context that EconModal etc. consume, and admin-gates on URL params). ──
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      let mut = false;
      if (!url.searchParams.get("UserID")) { url.searchParams.set("UserID", "admin"); mut = true; }
      if (!url.searchParams.get("Company")) { url.searchParams.set("Company", "lcs"); mut = true; }
      if (url.searchParams.get("LeaseID") !== selectedLeaseId) {
        url.searchParams.set("LeaseID", selectedLeaseId); mut = true;
      }
      if (url.searchParams.get("lease") !== selectedLeaseId) {
        url.searchParams.set("lease", selectedLeaseId); mut = true;
      }
      if (mut) window.history.replaceState({}, "", url.toString());
    } catch { /* noop */ }
  }, [selectedLeaseId]);

  // Recommendation panel content — curated profile wins, falls back to
  // deterministic inference, falls back to bundle row.
  const recoLabel = (
    curatedProfile?.primaryAction
    || inference?.recommendedAction
    || bundleEntry?.recommendation
    || "—"
  ).toUpperCase();
  const recoPriority = curatedProfile?.priority || null;
  const recoConfidence = (curatedProfile?.confidence || inference?.confidence || bundleEntry?.confidence || "low").toUpperCase();
  const recoHeadline = curatedProfile?.shortThesis || inference?.headline || bundleEntry?.summary || "";
  const recoWhy = curatedProfile?.whyItMatters || inference?.decisionSummary || bundleEntry?.summary || "";
  const recoProductionSignal = curatedProfile?.productionSignal || "";
  const recoEconSignal = curatedProfile?.econSignal || "";
  const recoOperatorTone = curatedProfile?.operatorTone || "";
  const recoRiskFlags = curatedProfile?.riskFlags || inference?.riskFlags || [];
  const recoNextSteps = (curatedProfile?.recommendedActions || inference?.operatorNextSteps || []).slice(0, 3);
  const recoEvidence = curatedProfile?.evidence || [];

  // Decision Inference Engine (data-grounded) — additive to the curated narrative.
  const recoInference = curatedProfile?.inference || null;
  const recoPriorityScore = recoInference?.priorityScore ?? null;
  const recoEconImpact = recoInference?.economicImpact || null;
  const recoFreshness = recoInference?.dataFreshness || null;
  const recoKeyDrivers = recoInference?.keyDrivers || [];

  // Structured decision card. When present, the right rail renders the
  // operator-first card layout (status / primary move / change chips / lever /
  // actions / flags) and the prose fields above collapse into a Detail drawer.
  const recoCard = curatedProfile?.decisionCard || null;
  // Forward-tracking Recommendation Ledger (Proof Ledger Lite).
  // Honest framing — no historical proof claims; outcomes pending.
  const recoLedger = curatedProfile?.ledger || null;
  const profileSource = curatedProfile?._source || (inference?.source === "deterministic_local" ? "deterministic" : "fallback");

  return (
    <div className="ck">
      {/* ═══ TOP TELEMETRY STRIP ═══ */}
      <header className="ck-top">
        <div className="ck-top-l">
          <span className="ck-logo" />
          <span className="ck-title">LCS OPERATIONAL INTELLIGENCE COCKPIT</span>
        </div>
        <div className="ck-top-c">
          <span className="ck-tel">
            <span className="ck-dot-live" />
            LCS · {lcs.loading ? "loading" : lcs.error ? "snapshot" : (view?.modelInfo?.staleness?.severity || "online")}
          </span>
          {view?.modelInfo?.staleness?.ageDays != null && (
            <span className="ck-tel">Forecast age: {view.modelInfo.staleness.ageDays}d</span>
          )}
          <span className="ck-tel">
            Source: {lcs.error
              ? "bundled snapshot"
              : `overlay_staged · ${RANKED_LEASES.filter((l) => l.assetTypeHint === "producer" && (l.forecastRows || 0) > 0).length} ranked`}
          </span>
        </div>
        <div className="ck-top-r">
          <div className="ck-avatar">JT</div>
          <span className="ck-user-name">LCS</span>
        </div>
      </header>

      {/* Breadcrumb */}
      <nav className="ck-bc">
        <span className="ck-bc-i">Home</span>
        <span className="ck-bc-s">›</span>
        <span className="ck-bc-i">LCS Portfolio</span>
        <span className="ck-bc-s">›</span>
        <span className="ck-bc-i ck-bc-a">{selectedLeaseId}</span>
      </nav>

      {/* ═══ MAIN BODY ═══ */}
      <div className="ck-body">

        {/* Icon Rail (compact) */}
        <nav className="ck-rail">
          {[
            { ic: "▣", title: "Decision Cockpit", active: true },
            { ic: "📊", title: "Portfolio", onClick: () => setPortfolioOpen(true) },
            { ic: "$", title: "Economics", onClick: () => setEconOpen(true) },
            { ic: "⚙", title: "Settings", active: false },
          ].map((b, i) => (
            <div
              key={i}
              className={`ck-rail-btn ${b.active ? "ck-rail-active" : ""}`}
              title={b.title}
              onClick={b.onClick}
              style={b.onClick ? { cursor: "pointer" } : {}}
            >
              <div className="ck-rail-icon">{b.ic}</div>
            </div>
          ))}
        </nav>

        {/* ═══ LEFT: LCS Lease Ranking ═══ */}
        <aside className="ck-left">
          <div className="ck-sec-title ck-left-title">LCS LEASE RANKING</div>
          <div style={{ marginBottom: 8 }}>
            <input
              type="text"
              value={ranklistFilter}
              onChange={(e) => setRanklistFilter(e.target.value)}
              placeholder="Filter…"
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "5px 8px", fontSize: 11,
                background: "rgba(10,16,32,0.7)",
                border: "1px solid rgba(40,65,110,0.22)",
                borderRadius: 4, color: "#c8d8e8",
                fontFamily: "'JetBrains Mono', monospace",
              }}
            />
          </div>

          <div className="ck-left-scroll">
            {rankedRows.length === 0 && (
              <div style={{ color: "#566580", fontSize: 11, padding: "12px 0" }}>
                No staged producers match.
              </div>
            )}
            {rankedRows.slice(0, 30).map((l, idx) => {
              const isSelected = l.leaseId === selectedLeaseId;
              const tier = SCORE_TIER(l.score || 0);
              const recoT = RECO_TIER(l.recommendation);
              const cardCls = isSelected
                ? "ck-rank-card ck-rank-top"
                : idx === 1
                  ? "ck-rank-card ck-rank-second"
                  : idx >= 5
                    ? "ck-rank-card ck-rank-dim"
                    : "ck-rank-card";
              const cf = (l.recentOilAvg || 0) * 70 + (l.recentGasAvg || 0) * 3;
              const isNeg = cf <= 0 && (l.recommendation === "Investigate Decline" || l.recommendation === "Monitor");
              return (
                <div
                  key={l.leaseId}
                  className={`${cardCls} ${isNeg ? "ck-rank-neg" : ""}`}
                  onClick={() => selectLease(l.leaseId)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="ck-rank-head">
                    <div className="ck-rank-meta">
                      <div className="ck-rank-well">{l.leaseId}</div>
                      <div className="ck-rank-basin">
                        {l.leaseName && l.leaseName !== l.leaseId
                          ? l.leaseName.length > 24 ? l.leaseName.slice(0, 24) + "…" : l.leaseName
                          : "LCS"}
                      </div>
                      <div className="ck-rank-sublabel">{l.recommendation || "—"}</div>
                    </div>
                    <div className="ck-score-wrap">
                      <div className={`ck-score ck-score-${tier}`}>{Math.round(l.score || 0)}</div>
                      <div className="ck-score-tag">
                        {l.score >= 75 ? "Demo" : l.score >= 55 ? "Tier 2" : "Watch"}
                      </div>
                    </div>
                  </div>
                  <div className="ck-rank-delta">
                    <div>
                      <div className="ck-rank-dlabel">Recent rate</div>
                      <div className={`ck-rank-dval ${cf > 100 ? "ck-pos" : ""}`}>
                        {fin(l.recentOilAvg) && l.recentOilAvg > 0
                          ? `${fmtN(l.recentOilAvg, 1)} bbl/d`
                          : fin(l.recentGasAvg) && l.recentGasAvg > 0
                            ? `${fmtN(l.recentGasAvg, 0)} mcf/d`
                            : "—"}
                      </div>
                    </div>
                    <Spark positive={cf > 100} score={l.score || 50} />
                  </div>
                  <div className="ck-rank-tags">
                    <span
                      className={`ck-tag ${
                        recoT === "good" ? "ck-tag-dim"
                          : recoT === "warn" ? "ck-tag-warn"
                            : "ck-tag-alert"
                      }`}
                    >
                      {l.recommendation || "—"}
                    </span>
                    {(() => {
                      // Prefer LIVE per-lease ageDays (refreshed by synthesizer)
                      // over the bundle's baked modelAgeDays. Only show the
                      // chip when the LIVE value is truly stale, so we don't
                      // contradict the right-panel's freshness.
                      const liveAge = LCS_LIVE_AGE_DAYS[l.leaseId];
                      const age = typeof liveAge === "number" ? liveAge : (l.modelAgeDays || 0);
                      return age > 30 ? (
                        <span className="ck-tag ck-tag-warn">{age}d stale</span>
                      ) : null;
                    })()}
                  </div>
                </div>
              );
            })}
          </div>

          <button
            onClick={() => setPortfolioOpen(true)}
            style={{
              marginTop: 8, width: "100%", padding: "8px 0",
              fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
              fontFamily: "'JetBrains Mono', monospace", textTransform: "uppercase",
              background: "rgba(34,211,238,0.10)",
              color: "#5ab8c9",
              border: "1px solid rgba(90,184,201,0.35)", borderRadius: 5, cursor: "pointer",
              flexShrink: 0,
            }}
          >
            Full portfolio · {RANKED_LEASES.filter((l) => l.assetTypeHint === "producer").length}
          </button>
        </aside>

        {/* ═══ CENTER: Production Forecast Hero ═══ */}
        <main className="ck-center">
          <div className="ck-chart-header">
            <div className="ck-chart-header-l">
              <span className="ck-chart-title">PRODUCTION FORECAST</span>
              <select
                className="ck-lease-sel"
                value={selectedLeaseId}
                onChange={(e) => selectLease(e.target.value)}
              >
                {RANKED_LEASES
                  .filter((l) => l.assetTypeHint === "producer" && (l.forecastRows || 0) > 0)
                  .map((l) => (
                    <option key={l.leaseId} value={l.leaseId}>
                      {l.leaseId} — {l.leaseName}
                    </option>
                  ))}
              </select>
            </div>
            <div className="ck-chart-pills">
              <span
                className={`ck-pill ${showOil ? "ck-pill-active" : ""}`}
                onClick={() => setShowOil((v) => !v)}
                style={{ color: showOil ? "#5afcb8" : undefined }}
              >Oil</span>
              <span
                className={`ck-pill ${showGas ? "ck-pill-active" : ""}`}
                onClick={() => setShowGas((v) => !v)}
                style={{ color: showGas ? "#f09898" : undefined }}
              >Gas</span>
              <span
                className={`ck-pill ${showWater ? "ck-pill-active" : ""}`}
                onClick={() => setShowWater((v) => !v)}
                style={{ color: showWater ? "#80c0f0" : undefined }}
              >Water</span>
              <span className="ck-pill-sep" />
              <span
                className={`ck-pill ${showBands ? "ck-pill-active" : ""}`}
                onClick={() => setShowBands((v) => !v)}
              >Bands</span>
              <span
                className={`ck-pill ${showMA ? "ck-pill-active" : ""}`}
                onClick={() => setShowMA((v) => !v)}
              >7D Avg</span>
            </div>
          </div>

          {/* KPI strip — compact, three streams + one econ marker */}
          <div className="ck-kpi-strip">
            {kpiRows.map((k) => (
              <div key={k.stream} className="ck-kpi-chip">
                <span className="ck-kpi-label">{k.stream}</span>
                <span className="ck-kpi-val">
                  {fin(k.latest) ? fmtN(k.latest, 1) : "—"} {k.unit}
                </span>
                {fin(k.trend) && Math.abs(k.trend) > 0.02 && (
                  <span
                    className={k.trend >= 0 ? "ck-pos" : "ck-neg"}
                    style={{ fontSize: 10 }}
                  >
                    {k.trend >= 0 ? "↑" : "↓"}
                    {Math.abs(k.trend * 100).toFixed(0)}%
                  </span>
                )}
              </div>
            ))}
            <div className="ck-kpi-chip">
              <span className="ck-kpi-label">Forecast</span>
              <span className="ck-kpi-val">
                {view?.modelInfo?.forecastWindow?.horizonDays || 60}d
              </span>
            </div>
            <div className="ck-kpi-chip">
              <span className="ck-kpi-label">Method</span>
              <span className="ck-kpi-val" style={{ fontSize: 11 }}>
                {Object.values(view?.modelInfo?.method || {})
                  .filter(Boolean)
                  .slice(0, 1)
                  .map((m) => m.replace(/_/g, " "))[0] || "—"}
              </span>
            </div>
          </div>

          {/* Hero chart — the visual anchor */}
          <LcsHeroChart
            view={view}
            showOil={showOil}
            showGas={showGas}
            showWater={showWater}
            showBands={showBands}
            showMA={showMA}
            loading={lcs.loading}
            emptyMessage={
              isOperationalLease
                ? `${selectedLeaseId} is classified as ${bundleEntry.assetTypeHint} — operational asset, no producer forecast.`
                : lcs.error
                  ? `Live overlay error · using bundled snapshot only.`
                  : `Select a producer lease to load forecast.`
            }
          />

          {/* Hidden ForecastDashboard — provides Context for EconModal etc.
               Keyed by selectedLeaseId so it remounts when the operator
               picks a different lease. URL is kept in sync above so the
               fresh FD instance reads ?lease=… on init. */}
          <div className="ck-fd-hidden">
            <ForecastDashboard key={`decision-fd-${selectedLeaseId}`} />
          </div>
        </main>

        {/* ═══ RIGHT: Decision System ═══ */}
        <aside className="ck-right">
          <div className="ck-sec-title">DECISION SYSTEM</div>

          {(() => {
            if (!USE_MINIMAL_DECISION_PANEL) return null;
            // 1. Hand-authored minimal profile (top 5 leases) wins.
            const hand = MINIMAL_LCS_DECISION_PROFILES[selectedLeaseId];
            if (hand) {
              return (
                <MinimalDecisionPanel
                  profile={hand}
                  onSimulate={() => setEconOpen(true)}
                  onPortfolio={() => setPortfolioOpen(true)}
                />
              );
            }
            // 2. Adapt the decisionCard (synthesized or curated) into the
            //    minimal shape so every other lease also renders the minimal
            //    panel. Returns null if there is no card at all.
            const adapted = adaptDecisionCardToMinimal({
              leaseId: selectedLeaseId,
              leaseName: bundleEntry?.leaseName || selectedLeaseId,
              card: recoCard,
              ledger: recoLedger,
              freshness: recoFreshness,
              priority: recoPriority,
              confidence: recoConfidence,
            });
            if (adapted) {
              return (
                <MinimalDecisionPanel
                  profile={adapted}
                  onSimulate={() => setEconOpen(true)}
                  onPortfolio={() => setPortfolioOpen(true)}
                />
              );
            }
            return null;
          })()}
          {!USE_MINIMAL_DECISION_PANEL && (recoCard ? (
            // ─── Structured Decision Card (operator-first) ────────────────
            <DecisionCard
              card={recoCard}
              ledger={recoLedger}
              priority={recoPriority}
              confidence={recoConfidence}
              priorityScore={recoPriorityScore}
              freshness={recoFreshness}
              recoWhy={recoWhy}
              recoOperatorTone={recoOperatorTone}
              onSimulate={() => setEconOpen(true)}
              onPortfolio={() => setPortfolioOpen(true)}
            />
          ) : (
            // ─── Fallback prose layout (when no structured card exists) ───
            <>
              <div className="ck-decision-block">
                <div className="ck-dec-row">
                  <div className="ck-dec-main">
                    <div className={`ck-dec-action ${recoTone === "neg" ? "ck-dec-neg" : "ck-dec-pos"}`}>
                      {recoLabel}
                    </div>
                    {recoPriority && (
                      <div style={{
                        marginTop: 4, display: "inline-block",
                        fontSize: 9, fontWeight: 800, padding: "2px 7px",
                        borderRadius: 3, fontFamily: "'JetBrains Mono', monospace",
                        letterSpacing: "0.06em",
                        background: recoPriority === "High" ? "rgba(232,85,85,0.10)"
                          : recoPriority === "Medium" ? "rgba(200,170,50,0.10)"
                          : "rgba(50,70,100,0.15)",
                        color: recoPriority === "High" ? "#e88888"
                          : recoPriority === "Medium" ? "#d8c050"
                          : "#7888a0",
                        border: `1px solid ${
                          recoPriority === "High" ? "rgba(232,85,85,0.18)"
                          : recoPriority === "Medium" ? "rgba(200,170,50,0.18)"
                          : "rgba(50,70,100,0.18)"}`,
                      }}>{recoPriority.toUpperCase()} PRIORITY</div>
                    )}
                  </div>
                  <div className={`ck-conf-badge ${
                    recoConfidence === "HIGH" ? "ck-conf-hi"
                      : recoConfidence === "MEDIUM" ? "ck-conf-mod"
                      : "ck-conf-lo"
                  }`}>{recoConfidence}</div>
                </div>
                {recoHeadline && <div className="ck-dec-diagnosis">{recoHeadline}</div>}
                {recoOperatorTone && (
                  <div style={{
                    marginTop: 6, padding: "5px 8px",
                    background: "rgba(90,184,201,0.06)",
                    borderLeft: "2px solid rgba(90,184,201,0.4)",
                    fontSize: 11, fontStyle: "italic", color: "#a8c0d8",
                    lineHeight: 1.35,
                  }}>“{recoOperatorTone}”</div>
                )}
                <div className="ck-cta-row">
                  <button className="ck-cta-1" onClick={() => setEconOpen(true)}>SIMULATE</button>
                  <button className="ck-cta-2" onClick={() => setPortfolioOpen(true)}>PORTFOLIO</button>
                </div>
              </div>

              {recoNextSteps.length > 0 && (
                <div className="ck-diag-block" style={{ marginTop: 8 }}>
                  <div className="ck-right-sub">NEXT STEPS</div>
                  <ol style={{ margin: 0, paddingLeft: 18, color: "#c8d8e8",
                                fontSize: 11, lineHeight: 1.5 }}>
                    {recoNextSteps.map((s, i) => <li key={i}>{s}</li>)}
                  </ol>
                </div>
              )}

              {recoRiskFlags.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div className="ck-right-sub">RISK FLAGS</div>
                  <div className="ck-evidence-chips">
                    {recoRiskFlags.slice(0, 4).map((f, i) => (
                      <span key={i} className="ck-ev-chip">{f}</span>
                    ))}
                  </div>
                </div>
              )}
            </>
          ))}

            <div style={{
              marginTop: 12, fontSize: 8, color: "#344260",
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: "0.04em", paddingTop: 6,
              borderTop: "1px solid rgba(25,42,75,0.18)",
            }}>
              ↳ {profileSource === "lcs_decision_profiles" ? "Curated LCS decision profile"
                : profileSource === "llm" ? "LLM commentary (live)"
                : profileSource === "deterministic" ? `Deterministic v${inference?.contractVersion || "1"}`
                : "Bundled inference"}
            </div>
        </aside>
      </div>

      {/* Modals */}
      {realLease && (
        <EconModal
          open={econOpen}
          onClose={() => setEconOpen(false)}
          realLease={realLease}
        />
      )}
      <LcsPortfolioTable
        open={portfolioOpen}
        onClose={() => setPortfolioOpen(false)}
        leases={RANKED_LEASES}
        selectedLeaseId={selectedLeaseId}
        onSelect={(id) => { selectLease(id); setPortfolioOpen(false); }}
      />
    </div>
  );
}

export default function ForecastDashboardDecisionUI() {
  return <CockpitInner />;
}
